import React, { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
  className?: string;
  /** Enable sticky header + vertical scroll for long tables. */
  sticky?: boolean;
};

/** The only element that may horizontally scroll table content. */
export default function DataTableScrollArea({ children, className = '', sticky = false }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowX, setOverflowX] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateOverflow = () => {
      setOverflowX(el.scrollWidth > el.clientWidth + 1);
    };

    updateOverflow();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    el.addEventListener('scroll', updateOverflow, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateOverflow);
    };
  }, [children]);

  const classes = [
    'data-table-scroll-area',
    'table-scroll-container',
    'responsive-table-container',
    'data-table-wrap',
    sticky ? 'data-table-wrap--sticky' : '',
    overflowX ? 'data-table-scroll-area--overflow-x' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} data-testid="table-scroll-container">
      {children}
    </div>
  );
}
